import React, { Suspense, useEffect, useMemo, lazy, useState } from "react";

import CssBaseline from "@mui/material/CssBaseline";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import * as Sentry from "@sentry/react";
import get from "lodash/get";
import has from "lodash/has";
import { Toaster } from "react-hot-toast";
import { useDispatch } from "react-redux";

import LoadingSpinner from "src/components/LoadingSpinner";
import SmartLoadingSpinner from "src/components/SmartLoadingSpinner";
import { config_gtmId } from "src/config";
import { PrivyContextProvider } from "src/contexts/PrivyContext";
import { WebSocketContextProvider } from "src/contexts/WebSocketContext";
import usePortalItem from "src/hooks/apis/usePortalItem";
import useGlobalLoading from "src/redux/hooks/useGlobalLoading";
import { setPrivyLogin } from "src/redux/slices/global";
import { createTheme } from "src/theme";
import { safeRequestIdleCallback } from "src/utils/preloader";

import Content from "./content";

// Lazy load PrivyProviderWrapper with idle callback for better performance
const PrivyProviderWrapper = lazy(() => new Promise((resolve, reject) => {
  safeRequestIdleCallback(() => {
    import(
      /* webpackChunkName: "core-components-deferred" */
      "src/components/PrivyProviderWrapper"
    )
      .then(resolve)
      .catch(reject);
  });
}));

const CoreView = () => {
  const { data, error, isLoading } = usePortalItem();
  const { disableLoading } = useGlobalLoading();
  const dispatch = useDispatch();
  const [hasError, setHasError] = useState(false);
  const portalItem = get(data, "portalItem", null);

  useEffect(() => {
    dispatch(setPrivyLogin(["email", "google", "apple"]));
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      Sentry.captureException(error, {
        tags  : { component: "Core", action: "portalItemLoad" },
        level : "error",
      });
      setHasError(true);
    }
  }, [error]);

  // Disable initial loading when portal data is loaded or when we have a definitive state
  useEffect(() => {
    if (portalItem || error || !isLoading) {
      disableLoading();
    }
  }, [portalItem, error, isLoading, disableLoading]);

  // Defer non-critical service initialization for better performance
  useEffect(() => {
    safeRequestIdleCallback(() => {
      // Defer GTM initialization (analytics)
      if (config_gtmId) {
        import(
          /* webpackChunkName: "core-components-deferred" */
          "react-gtm-module"
        ).then(({ default: TagManager }) => {
          try {
            TagManager.initialize({ gtmId: config_gtmId });
            console.log("[GTM] Initialized successfully");
          }
          catch (gtmError) {
            console.warn("[GTM] Failed to initialize:", gtmError);
          }
        }).catch((gtmLoadError) => {
          console.warn("[GTM] Failed to load module:", gtmLoadError);
        });
      }

      // Defer Intercom loading (customer support)
      import(
        /* webpackChunkName: "core-components-deferred" */
        "src/utils/intercom"
      ).then(({ default: loadIntercom }) => {
        try {
          loadIntercom();
          console.log("[Intercom] Initialized successfully");
        }
        catch (intercomError) {
          console.warn("[Intercom] Failed to initialize:", intercomError);
        }
      }).catch((intercomLoadError) => {
        console.warn("[Intercom] Failed to load module:", intercomLoadError);
      });
    });
  }, []);

  // Theme and logos from Series have priority over the ones from PortalItem
  const currentTheme = has(portalItem, "series.attributes.theme.primary")
    ? get(portalItem, "series.attributes.theme")
    : get(portalItem, "attributes.theme", null);
  const rectangleColoredLogo = has(portalItem, "series.attributes.theme.logos.rectangle_colored")
    ? get(portalItem, "series.attributes.theme.logos.rectangle_colored")
    : get(portalItem, "logos.rectangle_colored", null);
  const squareColoredLogo = has(portalItem, "series.attributes.theme.logos.square_colored")
    ? get(portalItem, "series.attributes.theme.logos.square_colored")
    : get(portalItem, "logos.square_colored", null);
  const theme = useMemo(() => createTheme({
    direction           : "ltr",
    paletteMode         : "light",
    responsiveFontSizes : true,
    roundedCorners      : true,
    colorPreset         : "indigo",
    contrast            : "high",
    orgTheme            : currentTheme
  }), [currentTheme]);

  useEffect(() => {
    if (portalItem) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.getElementsByTagName("head")[0].appendChild(link);
      }
      link.href = squareColoredLogo;
    }
  }, [portalItem, squareColoredLogo]);

  // Render with optimized provider architecture and smart loading
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <PrivyContextProvider>
          <PrivyProviderWrapper
            currentTheme={currentTheme}
            rectangleColoredLogo={rectangleColoredLogo}
          >
            <SmartLoadingSpinner>
              <WebSocketContextProvider>
                <Content
                  portalItem={portalItem}
                  hasError={hasError}
                />
              </WebSocketContextProvider>
            </SmartLoadingSpinner>
            <Toaster />
          </PrivyProviderWrapper>
        </PrivyContextProvider>
      </ThemeProvider>
    </Suspense>
  );
};

export default CoreView;
